import { z } from 'zod'

/**
 * Zod compiles object parsers with `new Function` when it may, and finds out
 * by trying once. The app's Content-Security-Policy allows no eval (the
 * Caddyfile), so the probe could only fail and be reported as a violation:
 * zod is told up front to use its interpreted parsers. Set here, the first
 * module to parse anything.
 */
z.config({ jitless: true })

/**
 * Environment is parsed once, at module load. A bad or missing variable fails
 * here — at startup, with the offending key named — rather than surfacing as a
 * confusing 404 on the first request an hour into a session.
 */
const envSchema = z.object({
  VITE_API_BASE_URL: z.url({ message: 'VITE_API_BASE_URL must be an absolute URL' }),
  /**
   * The Mitra AI engine, which is a SECOND ORIGIN — not a path under the
   * Django backend. OPTIONAL: the chat is one feature among many, and its
   * configuration must never be load-bearing for the rest of the app — see
   * `mitraApiBaseUrl` and `mitraLaunchersEnabled` below. (It was briefly
   * required with no default, which meant a build that forgot to pass it —
   * as the staging Dockerfile did — threw at module load and took down
   * every screen, not just the chat. That is the bug this schema now
   * prevents structurally rather than by remembering to pass the arg.)
   * Still validated as an absolute URL WHEN present: a build that silently
   * pointed the panel at the wrong host should still fail loudly.
   *
   * `z.preprocess` folds an empty string into `undefined` before the URL
   * check runs, which is not a hypothetical: an unset Dockerfile `ARG` with
   * no default is not simply absent from `process.env` at build time — it
   * is set to `''` (measured directly: `ARG X` + `ENV X=$X` with no
   * `--build-arg` gives `process.env.X === ''`, not `X in process.env ===
   * false`), and Vite's `loadEnv` then merges that empty string over
   * whatever `.env` supplied, because it iterates `process.env` with `for
   * ... in` — a key present with value `''` is still present. Without this
   * preprocessing, `.optional()` alone would only excuse a TRULY missing
   * key, and the exact Coolify build this fix targets — the build arg left
   * out — would still hand `''` to `z.url()` and still throw at module
   * load. Same crash, different message.
   */
  VITE_MITRA_API_BASE_URL: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.url({ message: 'VITE_MITRA_API_BASE_URL must be an absolute URL' }).optional(),
  ),
  VITE_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  /**
   * Whether the chat asks the engine for its json-render cards (`'1'`).
   * Folded from `''` for the reason `VITE_MITRA_API_BASE_URL` is: an unset
   * Docker ARG arrives as the empty string, and that must read as off.
   */
  VITE_MITRA_GENUI: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['0', '1']).optional(),
  ),
  /**
   * Whether the chat may draw Mitra's answers as a Stage (`'1'`), the page
   * beside the thread (stage foundation design §3.2). Folded from `''` for
   * the reason `VITE_MITRA_API_BASE_URL` is.
   */
  VITE_MITRA_STAGE: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['0', '1']).optional(),
  ),
  /** Playable cards, stories and the pen (interactive shapes §15.1): optional, folded from `''`. */
  VITE_MITRA_PLAY: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['0', '1']).optional(),
  ),
  VITE_MITRA_STORY: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['0', '1']).optional(),
  ),
  /** The general answer page (wire §16): optional, folded from `''`. */
  VITE_MITRA_ANSWER_PAGE: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['0', '1']).optional(),
  ),
  VITE_MITRA_PEN: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['0', '1']).optional(),
  ),
  /** An answer's Listen control (the engine reads it back): optional, folded from `''`. */
  VITE_MITRA_LISTEN: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['0', '1']).optional(),
  ),
  /** An answer's Details for staff (the turn's trace): optional, folded from `''`. */
  VITE_MITRA_TRACE: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['0', '1']).optional(),
  ),
  /**
   * The Firebase web app that turns a Google or Apple sign-in into the ID token mm-be's
   * POST /auth/social/ verifies. OPTIONAL, all four or none: see `firebase`
   * below. Folded from `''` for the reason `VITE_MITRA_API_BASE_URL` is.
   */
  VITE_FIREBASE_API_KEY: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.string().optional(),
  ),
  VITE_FIREBASE_AUTH_DOMAIN: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.string().optional(),
  ),
  VITE_FIREBASE_PROJECT_ID: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.string().optional(),
  ),
  VITE_FIREBASE_APP_ID: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.string().optional(),
  ),
  /**
   * Web push, on top of the four above: see `push` below. The sender id is the
   * project number (the second part of the app id). The VAPID key is the
   * project's Web Push certificate; unset, Firebase uses its shared default.
   */
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.string().optional(),
  ),
  VITE_FIREBASE_VAPID_KEY: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.string().optional(),
  ),
  /**
   * Where company logos live: `{base}{SYMBOL}.png`. OPTIONAL, defaulting to
   * the CDN's `/media/` path — the only one that serves them (measured
   * 2026-09-28: `/media/NABIL.png` is a 200 `image/png`, the bare
   * `/NABIL.png` the API's own `logo_url` spells is a 404). Folded from `''`
   * for the reason `VITE_MITRA_API_BASE_URL` is, so an unset Docker ARG
   * reads as "use the default", not as an invalid URL.
   */
  VITE_COMPANY_LOGO_BASE_URL: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z
      .url({ message: 'VITE_COMPANY_LOGO_BASE_URL must be an absolute URL' })
      .default('https://cdn.moneymitra.com/media/'),
  ),
})

const parsed = envSchema.safeParse(import.meta.env)

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((i) => `  ${i.path.join('.') || '(root)'}: ${i.message}`)
    .join('\n')
  throw new Error(`Invalid environment. Check your .env file:\n${issues}`)
}

type Env = z.infer<typeof envSchema>

/** All four or nothing: a half-set config would only fail on a member's click, so it reads as unset. */
const firebaseFrom = (env: Env) => {
  const apiKey = env.VITE_FIREBASE_API_KEY
  const authDomain = env.VITE_FIREBASE_AUTH_DOMAIN
  const projectId = env.VITE_FIREBASE_PROJECT_ID
  const appId = env.VITE_FIREBASE_APP_ID
  if (
    apiKey === undefined ||
    authDomain === undefined ||
    projectId === undefined ||
    appId === undefined
  ) {
    return null
  }
  const messagingSenderId = env.VITE_FIREBASE_MESSAGING_SENDER_ID
  return {
    apiKey,
    authDomain,
    projectId,
    appId,
    ...(messagingSenderId === undefined ? {} : { messagingSenderId }),
  }
}

const firebase = firebaseFrom(parsed.data)

export const config = {
  /** Absolute origin of the Django backend. */
  apiBaseUrl: parsed.data.VITE_API_BASE_URL,
  /**
   * Absolute origin of the Mitra AI engine, or `null` when the build did not
   * configure one.
   *
   * Modelled as `null`, not `undefined` and not `''`. `undefined` would read
   * as "nobody checked yet"; this IS checked, and the fact is "there isn't
   * one". `''` is worse than either — it is falsy like `null` but is also a
   * valid operand for string concatenation, so a call site that forgot to
   * guard would silently build `/v1/ask` or `https:///v1/ask` instead of
   * failing to build anything. `null` reads as neither a path nor a host, so
   * it cannot be concatenated into something that merely LOOKS like a URL.
   */
  mitraApiBaseUrl: parsed.data.VITE_MITRA_API_BASE_URL ?? null,

  /**
   * The Firebase web config for Google and Apple sign-in, or `null` when the
   * build left any of its four values out — then both buttons keep their "Coming
   * soon". It must be the Firebase project mm-be's service account belongs
   * to: mm-be verifies the ID token against that project and no other.
   */
  firebase,
  /**
   * Web push for this browser, or `null` when the build cannot do it: no
   * Firebase config, or no sender id. `vapidKey: null` lets Firebase use its
   * shared default key. Null keeps the Settings card and every registration
   * call out of the build's behaviour entirely.
   */
  push:
    firebase?.messagingSenderId === undefined
      ? null
      : { vapidKey: parsed.data.VITE_FIREBASE_VAPID_KEY ?? null },
  env: parsed.data.VITE_ENV,
  isDev: import.meta.env.DEV,

  /**
   * Whether the four Mitra AI launchers (rail, drawer, top nav, Home card)
   * open the panel, or keep the inert "Coming soon" affordance they carried
   * before it existed. No flag of its own: on exactly when an engine origin
   * was baked into the bundle, in every environment, production included.
   *
   * A button that cannot reach an engine must not be live. `VITE_ENV` alone
   * used to gate this, which is exactly how staging shipped four working-
   * looking buttons wired to an engine origin that was never baked into the
   * bundle: a *config* bug (see `VITE_MITRA_API_BASE_URL` above). The engine
   * origin is therefore the whole gate, and a production build that states
   * one ships Mitra; one that leaves it out ships none.
   */
  mitraLaunchersEnabled: parsed.data.VITE_MITRA_API_BASE_URL !== undefined,

  /**
   * Whether the chat asks the engine for the answer's card as json-render
   * patches (`accepts: ['status', 'spec']`) and draws it. On only when the
   * build states `'1'`, in production as elsewhere: a feature reaches members
   * only by a build argument someone set on purpose, never by a default. Off,
   * the ask is byte-identical to the build before this flag existed.
   */
  mitraGenui: parsed.data.VITE_MITRA_GENUI === '1',

  /**
   * Whether the chat states `accepts: ['stage']` and draws a Stage, which it
   * does only where the engine ALSO says so (`capabilities.stage`, wire
   * contract §14.1): both switches, or nothing changes. On only when the
   * build states `'1'`, and it needs `mitraGenui`: a Stage's sections are the
   * card's elements. Off, every request and every render is the one the
   * build before it made.
   */
  mitraStage: parsed.data.VITE_MITRA_GENUI === '1' && parsed.data.VITE_MITRA_STAGE === '1',

  /**
   * Whether the chat states `accepts: ['play']` and draws playable cards,
   * only where the engine says `capabilities.play` too (interactive shapes
   * §15.1). On only when the build states `'1'`; it needs `mitraGenui`,
   * since a play patches the card's spec.
   */
  mitraPlay: parsed.data.VITE_MITRA_GENUI === '1' && parsed.data.VITE_MITRA_PLAY === '1',
  /** Stories: a Stage told as a story, so it needs the Stage switch too. */
  mitraStory:
    parsed.data.VITE_MITRA_GENUI === '1' &&
    parsed.data.VITE_MITRA_STAGE === '1' &&
    parsed.data.VITE_MITRA_STORY === '1',
  /**
   * The general answer page: a Stage of the server's own cards, so it needs
   * the Stage switch too. On only when the build states `'1'`.
   */
  mitraAnswerPage:
    parsed.data.VITE_MITRA_GENUI === '1' &&
    parsed.data.VITE_MITRA_STAGE === '1' &&
    parsed.data.VITE_MITRA_ANSWER_PAGE === '1',
  /** The teacher's pen over a drawn card; same shape as `mitraPlay`. */
  mitraPen: parsed.data.VITE_MITRA_GENUI === '1' && parsed.data.VITE_MITRA_PEN === '1',
  /**
   * Whether an answer may offer Details: how the turn was made, step by step,
   * with the prompts the model was sent (`GET /v1/turns/{id}/trace`). Off by
   * default, and off in production whatever the build states: it is a staff
   * tool, and a production bundle carries no way to ask for it. Elsewhere it
   * still stands only where the engine says THIS reader may see it
   * (`capabilities.trace`, on the ack or `GET /v1/capabilities`); the engine
   * answers 404 to anyone who is not staff, so the flag is never the gate
   * that keeps a prompt from a member, only the switch that lets the client
   * ask at all.
   */
  mitraTrace: parsed.data.VITE_ENV !== 'production' && parsed.data.VITE_MITRA_TRACE === '1',

  /**
   * Whether an answer's row offers Listen, the engine reading the answer
   * back. Off by default (owner, 2026-10-25: "hid this"); the voice it asks
   * for is unchanged where it is turned on.
   */
  mitraListen: parsed.data.VITE_MITRA_LISTEN === '1',

  /**
   * In dev, requests go to the Vite proxy at /api so they stay same-origin.
   * In a build they go straight to the backend origin.
   */
  apiPrefix: import.meta.env.DEV ? '/api/v5' : `${parsed.data.VITE_API_BASE_URL}/api/v5`,

  /**
   * The same shape as apiPrefix, for the engine. The version segment is NOT
   * folded in here the way /api/v5 is: the engine serves /v1/ask and
   * /v1/turns/* off its root, so the prefix is the origin and askClient.ts
   * spells the routes.
   *
   * The engine carries no CORS middleware, so in dev the Vite proxy at /mitra
   * is not a convenience — it is the only way the browser reaches it at all.
   * A build calls the origin directly and needs CORS there (see the report).
   *
   * The dev branch is unconditional — `/mitra` regardless of whether
   * `VITE_MITRA_API_BASE_URL` is set — because `vite.config.ts`'s proxy
   * already carries its own local fallback target, so local dev is
   * unaffected by this file's change. The build branch mirrors
   * `mitraApiBaseUrl`: `null`, not a fake origin, when unconfigured.
   * `askClient.ts` is the only reader and asserts non-null before use,
   * which is safe in practice because `mitraLaunchersEnabled` above keeps
   * every caller of it unreachable when this is `null`.
   */
  mitraPrefix: import.meta.env.DEV ? '/mitra' : (parsed.data.VITE_MITRA_API_BASE_URL ?? null),

  /**
   * The company-logo base, always ending in `/`. Normalised here rather than
   * demanded of whoever sets the variable: `company-logos.ts` appends a bare
   * file name, so a base without its slash would silently build
   * `…/mediaNABIL.png`, a 404 that falls back to the letter mark and looks
   * like nothing more than a missing logo.
   */
  companyLogoBaseUrl: parsed.data.VITE_COMPANY_LOGO_BASE_URL.endsWith('/')
    ? parsed.data.VITE_COMPANY_LOGO_BASE_URL
    : `${parsed.data.VITE_COMPANY_LOGO_BASE_URL}/`,

  /**
   * Poll interval while the market is open. The backend refreshes live prices
   * on a 60s cron and market depth on 30s, so anything faster than this spends
   * bandwidth to receive identical bytes.
   */
  livePollMs: 20_000,
} as const
